import { type DocumentProps, pdf } from '@react-pdf/renderer';
import Cookies from 'js-cookie';
import { type ReactElement } from 'react';
import { STORAGE_PREFIX, COOKIES } from '@avanta/shared/constants/storage';

const COOKIE_HTTP_ONLY = import.meta.env.VITE_COOKIE_HTTP_ONLY === 'true';

const COOKIE_ATTRIBUTES = {
  path: '/',
  httpOnly: COOKIE_HTTP_ONLY,
  secure: true,
  sameSite: 'Strict' as const,
};

export interface StorageUtils {
  getUserId: () => string;
  getRefreshToken: () => string | undefined;
  getAnonymousTokenCookie: () => string | undefined;
  removeAllCookies: () => void;
  setCookieWithExpiry: (key: string, value: string, expiresInSeconds: number) => void;
  setCookieWithoutExpiry: (key: string, value: string) => void;
}

export const createStorageUtils = (storagePrefix: string): StorageUtils => ({
  getUserId: (): string => Cookies.get(`${storagePrefix}${COOKIES.USER_ID}`) || '',
  getRefreshToken: (): string | undefined =>
    Cookies.get(`${storagePrefix}${COOKIES.REFRESH_TOKEN}`),
  getAnonymousTokenCookie: (): string | undefined =>
    Cookies.get(`${storagePrefix}${COOKIES.ANONYMOUS_TOKEN}`),
  removeAllCookies: (): void => {
    Object.values(COOKIES).forEach(cookie =>
      Cookies.remove(`${storagePrefix}${cookie}`, COOKIE_ATTRIBUTES)
    );
  },
  setCookieWithExpiry: (key: string, value: string, expiresInSeconds: number): void => {
    Cookies.set(`${storagePrefix}${key}`, value, {
      ...COOKIE_ATTRIBUTES,
      expires: expiresInSeconds / (60 * 60 * 24),
    });
  },
  setCookieWithoutExpiry: (key: string, value: string): void => {
    Cookies.set(`${storagePrefix}${key}`, value, {
      ...COOKIE_ATTRIBUTES,
      expires: 36500, // ~100 years
    });
  },
});

export const openPDF = async ({
  file,
  filename,
  pdfComponent,
}: {
  file?: Blob;
  filename: string;
  pdfComponent?: ReactElement<DocumentProps>;
}): Promise<void> => {
  let blob: Blob | undefined;
  if (pdfComponent) {
    blob = await pdf(pdfComponent).toBlob();
  }
  if (file) {
    blob = file;
  }
  if (!blob) {
    throw new Error('No file provided');
  }
  const url = URL.createObjectURL(blob);
  const newWindow = window.open(url, '_blank');

  if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename}.pdf`;
    link.click();
  }
};

type DownloadFileParams = {
  content: Blob | string;
  filename: string;
  mimeType?: string;
};

export const downloadFile = ({
  content,
  filename,
  mimeType = 'application/octet-stream',
}: DownloadFileParams): void => {
  const blob = content instanceof Blob ? content : new Blob([content], { type: mimeType });
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
};

let storageUtils = createStorageUtils(STORAGE_PREFIX);

/** Align shared cookie helpers with the host app prefix (call once at app bootstrap). */
export const configureStorage = (storagePrefix: string): void => {
  storageUtils = createStorageUtils(storagePrefix);
};

export const getUserId = (): string => storageUtils.getUserId();
export const getRefreshToken = (): string | undefined => storageUtils.getRefreshToken();
export const getAnonymousTokenCookie = (): string | undefined =>
  storageUtils.getAnonymousTokenCookie();
export const removeAllCookies = (): void => storageUtils.removeAllCookies();
export const setCookieWithExpiry = (key: string, value: string, expiresInSeconds: number): void =>
  storageUtils.setCookieWithExpiry(key, value, expiresInSeconds);
export const setCookieWithoutExpiry = (key: string, value: string): void =>
  storageUtils.setCookieWithoutExpiry(key, value);
